/** @jsx jsx */
import { Box, CTAButton, Flex, Heading, jsx, Link, Paragraph } from '@themed';

import { SvgIcon } from '@core';
import gsap from 'gsap';
import { MorphSVGPlugin } from 'gsap/all';
import { SplitText } from 'gsap/SplitText';
import { useLayoutEffect } from 'react';
import AOELogo from 'src/images/icons/logo/AOELogo';
import FinnoLogoWhite from 'src/images/icons/logo/FinnoLogoWhite';
import { useNewEntityName } from 'src/modules/hooks';

/**
 * Calculates the x and y offsets between two elements using getBoundingClientRect()
 */
function calculateOffsets(source: HTMLElement, target: HTMLElement): { x: number; y: number } {
  const sourceRect = source.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  return {
    x: targetRect.left - sourceRect.left,
    y: targetRect.top - sourceRect.top,
  };
}

/**
 * Determines which axis should have the yoyo effect based on offset magnitudes.
 * The axis with smaller absolute offset gets the yoyo, the larger one moves directly.
 */
function determineYoyoAxis(offsets: { x: number; y: number }): 'x' | 'y' {
  return Math.abs(offsets.x) > Math.abs(offsets.y) ? 'y' : 'x';
}

export const AnnounceTransformToAOE = () => {
  const { newEntityName, oldEntityName } = useNewEntityName();

  useLayoutEffect(() => {
    gsap.registerPlugin(MorphSVGPlugin);

    // Erase all GSAP-applied inline styles on page load to reset to original state
    gsap.killTweensOf('*');

    // Kill morph SVG animations specifically
    const morphElements = ['#finnoconsult-letters-fi', '#finnoconsult-letters-nn', '#finnoconsult-letters-oconsult'];
    gsap.killTweensOf(morphElements);

    // Reset morph SVG paths - MorphSVG modifies the 'd' attribute, clearing it reverts to original
    // Also clear 'fill' since that's animated in the morph
    gsap.set(morphElements, { clearProps: 'd,fill' });

    // Clear all other properties from all elements
    gsap.set(
      [
        '#finnoconsult-logo',
        '#aoe-logo',
        '#text-becomes',
        '#finnoconsult-letters-fi',
        '#finnoconsult-letters-nn',
        '#finnoconsult-letters-oconsult',
        '#aoe-box',
        '#aoe-letters-e',
        '#aoe-letters-ao',
      ],
      { clearProps: 'all' }
    );

    // Re-apply initial hidden states after clearProps (since clearProps removes inline styles from sx)
    gsap.set('#aoe-logo', { visibility: 'hidden' });
    gsap.set('#text-becomes', { opacity: 0, zIndex: 0 });
    gsap.set('#announcement-details > *', { opacity: 0, y: -50 });

    gsap.registerPlugin(SplitText);

    const textSplit = new SplitText('#text-becomes', { type: 'chars' });

    // Calculate offsets and determine animation axes
    const finnoconsultLogo = document.querySelector('#finnoconsult-logo') as HTMLElement;
    const aoeLogo = document.querySelector('#aoe-logo') as HTMLElement;
    const offsets = finnoconsultLogo && aoeLogo ? calculateOffsets(finnoconsultLogo, aoeLogo) : { x: 0, y: 0 };
    const yoyoAxis = determineYoyoAxis(offsets);
    const directAxis = yoyoAxis === 'x' ? 'y' : 'x';
    const directOffset = yoyoAxis === 'x' ? offsets.y : offsets.x;

    // Start animation immediately with no delay
    gsap
      .timeline({
        defaults: { duration: 1.6, ease: 'power2.inOut' },
        delay: 0.6,
      })
      .to('#finnoconsult-logo', { scale: 0.97, yoyo: true, repeat: 2, duration: 0.1, ease: 'circ' })
      .to('#finnoconsult-logo', { scale: 1, duration: 0.1, ease: 'power2.inOut' })
      .to({}, { duration: 0.6 }) // wait
      .to('#finnoconsult-logo', { scale: 0.97, yoyo: true, repeat: 2, duration: 0.1, ease: 'power2.inOut' })
      .to('#finnoconsult-logo', { scale: 1, duration: 0.1, ease: 'power2.inOut' })
      .to('#text-becomes', { opacity: 1, duration: 0.01 })
      // Animate each letter in 'becomes' as a typing horizontal reveal, staggered
      .from(textSplit.chars, {
        opacity: 0,
        translateX: '-150%',
        transformOrigin: 'left center',
        duration: 0.3,
        stagger: 0.1,
      })
      // Yoyo animation on the axis with smaller offset
      .to('#finnoconsult-logo', {
        [yoyoAxis]: `${yoyoAxis === 'y' ? '-' : ''}66px`,
        yoyo: true,
        repeat: 1,
        duration: 0.8,
        ease: 'power2.inOut',
      })
      // Direct movement on the axis with larger offset
      .to(
        '#finnoconsult-logo',
        {
          [directAxis]: directOffset,
        },
        '<'
      )
      .to(
        '#finnoconsult-letters-fi',
        {
          morphSVG: '#aoe-box',
          fill: '#ffffff',
        },
        '<'
      )
      .to(
        '#finnoconsult-letters-nn',
        {
          morphSVG: '#aoe-letters-e',
          fill: '#ffffff',
        },
        '<'
      )
      .to(
        '#finnoconsult-letters-oconsult',
        {
          morphSVG: '#aoe-letters-ao',
          fill: '#ffffff',
        },
        '<'
      )
      .to('#text-becomes', { opacity: 0, scaleX: 0, transformOrigin: 'center', duration: 0.6 })
      // Reverse animation: yoyo back on the same axis
      .to(
        '#finnoconsult-logo',
        {
          [yoyoAxis]: `${yoyoAxis === 'y' ? '66px' : '-10px'}`,
          yoyo: true,
          scale: 1.7,
          repeat: 1,
          duration: 0.8,
          ease: 'power2.out',
        },
        '-=0.3'
      )
      .to('#finnoconsult-logo', { [directAxis]: '0' }, '<')
      .to('#announcement-details > *', { opacity: 1, translateY: 0, stagger: 0.5, duration: 0.6 }, '-=2');
  }, []);

  return (
    <Box variant='styles.blogTextMaxWidth'>
      <Box sx={{ position: 'relative', height: '80px', overflow: 'visible' }}>
        <Flex
          sx={{
            flexDirection: ['column', 'column', 'row'],
            justifyContent: 'center',
            alignItems: 'center',
            gap: 4,
          }}
        >
          {/* Finnoconsult logo */}
          <FinnoLogoWhite id='finnoconsult-logo' height='70' />
          {/* Hidden via CSS class only when JS is enabled - visible if JS disabled */}
          <Heading id='text-becomes' as='h1' className='js-animate-reveal'>
            becomes
          </Heading>
          {/* AOE logo - hidden via CSS class only when JS is enabled */}
          <AOELogo id='aoe-logo' height='66' className='js-hidden' />
        </Flex>
      </Box>

      {/* Children hidden via CSS class only when JS is enabled */}
      <Box id='announcement-details' className='js-animate-children' variant='flex.colCenter' py={6}>
        <Heading as='h3' variant='styles.h4'>
          <strong>{oldEntityName}</strong> has been part of the <strong>AOE Group </strong>since 2024.
        </Heading>

        <Heading as='h3' variant='styles.h4'>
          We are now operating under the name <strong>{newEntityName}</strong>.
        </Heading>

        <Paragraph>
          With this step, we are bundling our expertise under one strong brand and creating central access <br />
          to our entire range of services. For you, this means: one brand, one portfolio, one location.
        </Paragraph>

        <Paragraph>
          You can find our entire range of services at
          <Link href='https://www.aoe.com' sx={{ color: 'black' }}>
            {' '}
            www.aoe.com
          </Link>
          <br />
          You can find Innoscore (formerly Finnoscore) at
          <Link href='https://www.innoscore.com' sx={{ color: 'black' }}>
            {' '}
            www.innoscore.com
          </Link>
        </Paragraph>

        <Box variant='flex.center' py={6}>
          <a href='https://www.aoe.com' target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none' }}>
            <CTAButton as='span' variant='ctaInvert'>
              AOE
              <SvgIcon name='arrow-right' sx={{ pl: 3 }} />
            </CTAButton>
          </a>
          <a href='https://www.innoscore.com' target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none' }}>
            <CTAButton as='span' variant='ctaInvert'>
              Innoscore
              <SvgIcon name='arrow-right' sx={{ pl: 3 }} />
            </CTAButton>
          </a>
        </Box>
      </Box>
    </Box>
  );
};
